import React from 'react';

const DarkSectorSection = () => {
  return (
    <section style={{
      background: '#000000',
      padding: '100px 7.6923%',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <h2 className="section-title" style={{
          fontSize: '48px',
          fontWeight: 600,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          color: '#FFFFFF',
          marginBottom: '60px',
          textAlign: 'center'
        }}>
          THE DARK SECTOR
        </h2>

        <div className="two-col-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div className="section-image" style={{
            position: 'relative',
            height: '400px',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.25)'
          }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_omega-theory/artifacts/t3ajv6ir_20251217_2104_Cosmic%20Energy%20Grid_simple_compose_01kcqpksgpe0mbny220ekxq4em.png" 
              alt="Cosmic Energy Grid"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.9
              }}
            />
          </div>

          <div>
            <h3 className="subsection-title" style={{
              fontSize: '32px',
              fontWeight: 600,
              lineHeight: 1.25,
              color: '#FFFFFF',
              marginBottom: '24px'
            }}>
              Dark Energy as Computational Overhead
            </h3>
            
            <p className="content-text" style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '20px'
            }}>
              The accelerating expansion of the universe doesn't require a cosmological constant. In Universe Engine Theory, it emerges from a single dark sector—one complex field with a minimal sextic self-interaction that produces three distinct phases:
            </p>

            <div style={{ marginBottom: '24px' }}>
              <div style={{
                background: 'rgba(0, 255, 209, 0.1)',
                border: '1px solid #00FFD1',
                padding: '16px',
                marginBottom: '12px'
              }}>
                <span style={{ color: '#00FFD1', fontWeight: 600 }}>U-Phase:</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.85)', marginLeft: '12px' }}>A wave-dominated early phase</span>
              </div>
              <div style={{
                background: 'rgba(0, 255, 209, 0.1)',
                border: '1px solid #00FFD1',
                padding: '16px',
                marginBottom: '12px'
              }}>
                <span style={{ color: '#00FFD1', fontWeight: 600 }}>X-Phase:</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.85)', marginLeft: '12px' }}>A particle phase during structure formation</span>
              </div>
              <div style={{
                background: 'rgba(0, 255, 209, 0.1)',
                border: '1px solid #00FFD1',
                padding: '16px'
              }}>
                <span style={{ color: '#00FFD1', fontWeight: 600 }}>A-Phase:</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.85)', marginLeft: '12px' }}>A fluid phase driving late-time acceleration</span>
              </div>
            </div>

            <h4 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#00FFD1',
              marginBottom: '16px',
              marginTop: '32px'
            }}>
              The Physical Mechanism
            </h4>

            <p className="content-text" style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '20px'
            }}>
              When the field enters its late-time A-phase, coarse-graining produces an effective viscous fluid with a radiative sink. In structure growth, this appears as a retarded drag term that can only suppress growth relative to standard models.
            </p>

            <p className="content-text" style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '24px'
            }}>
              The result is a universe that expands faster and forms structure more slowly than ΛCDM predicts. The S₈ tension isn't an anomaly—it's the observational signature of the A-phase transition itself.
            </p>

            <h4 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#00FFD1',
              marginBottom: '16px'
            }}>
              The Computational Interpretation
            </h4>

            <p className="content-text" style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '20px'
            }}>
              The deeper question is why this particular history is realized at all. Universe Engine Theory treats the cosmos as a system in which only histories that remain globally self-consistent and compressible across scales are admissible. This criterion is encoded in a variational principle balancing predictive success against complexity.
            </p>

            <p className="content-text" style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '24px'
            }}>
              As cosmic structure accumulates, histories that remain admissible naturally exhibit a late-time A-phase. The associated dissipation is not literal waste heat, but the physical expression of the informational constraints that govern which global histories can exist.
            </p>

            <p className="content-text" style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '24px',
              fontStyle: 'italic'
            }}>
              The computational idea explains why this expansion history is selected. The A-phase explains how acceleration happens physically. They are related—but distinct.
            </p>

            <h4 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#00FFD1',
              marginBottom: '16px'
            }}>
              What This Means
            </h4>

            <p className="content-text" style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)'
            }}>
              The universe doesn't accelerate because space is filled with vacuum energy. It accelerates because of how its information is organized over time. Dark energy isn't a substance—it's a consequence of which cosmic histories are able to remain globally consistent as complexity emerges.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          section {
            padding: 60px 20px !important;
          }
          .section-title {
            font-size: 36px !important;
          }
          .subsection-title {
            font-size: 28px !important;
          }
          .two-col-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .section-image {
            height: 300px !important;
            order: 2;
          }
          .two-col-grid > div:not(.section-image) {
            order: 1;
          }
        }
        @media (max-width: 768px) {
          section {
            padding: 40px 20px !important;
          }
          .section-title {
            font-size: 32px !important;
          }
          .subsection-title {
            font-size: 24px !important;
          }
          .content-text {
            font-size: 16px !important;
          }
          .section-image {
            height: 250px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default DarkSectorSection;